import { getWizardDefinition } from "@/app/wizard/wizard-engine";
import type {
  WizardI18nConfigWithLocales,
  WizardI18nLanguage,
} from "@/app/wizard/wizard-types";

import { DEFAULT_I18N_CONFIG } from "./config";

// ============================================================================
// Cache Implementation
// ============================================================================

let i18nConfigCache: WizardI18nConfigWithLocales | null = null;

/**
 * Clear the i18n configuration cache
 * Useful for testing or hot reloading in development
 */
export function clearI18nConfigCache(): void {
  i18nConfigCache = null;
}

// ============================================================================
// I18n Configuration Functions
// ============================================================================

/**
 * Get i18n configuration from wizard definition
 * Automatically derives supportedLocales from languages array
 * Results are cached for performance
 */
export async function getWizardI18nConfig(): Promise<WizardI18nConfigWithLocales> {
  // Return cached config if available
  if (i18nConfigCache) {
    return i18nConfigCache;
  }

  const wizard = await getWizardDefinition(null);

  // Get base config (either from wizard or default)
  const baseConfig = wizard.i18n ?? DEFAULT_I18N_CONFIG;

  // Always derive supportedLocales from languages array
  const supportedLocales = baseConfig.languages.map((lang) => lang.code);

  // Build and cache the config
  i18nConfigCache = {
    defaultLocale: baseConfig.defaultLocale,
    languages: baseConfig.languages,
    supportedLocales, // Always derived, never from JSON
  };

  return i18nConfigCache;
}

/**
 * Get supported locales from wizard
 * Derived from languages array
 */
export async function getWizardSupportedLocales(): Promise<string[]> {
  const i18n = await getWizardI18nConfig();
  return i18n.supportedLocales;
}

/**
 * Get default locale from wizard
 */
export async function getWizardDefaultLocale(): Promise<string> {
  const i18n = await getWizardI18nConfig();
  return i18n.defaultLocale;
}

/**
 * Get all language configurations from wizard
 */
export async function getWizardLanguages(): Promise<WizardI18nLanguage[]> {
  const i18n = await getWizardI18nConfig();
  return i18n.languages;
}

/**
 * Get language configuration by locale code
 */
export async function getWizardLanguageByCode(
  locale: string
): Promise<WizardI18nLanguage | undefined> {
  const languages = await getWizardLanguages();
  return languages.find((lang) => lang.code === locale);
}

/**
 * Check if a locale is supported by the wizard
 */
export async function isLocaleSupportedByWizard(
  locale: string
): Promise<boolean> {
  const supportedLocales = await getWizardSupportedLocales();
  return supportedLocales.includes(locale);
}

/**
 * Validate and get safe locale (fallback to default if not supported)
 */
export async function getSafeLocale(locale: string): Promise<string> {
  const isSupported = await isLocaleSupportedByWizard(locale);

  if (isSupported) {
    return locale;
  }

  return getWizardDefaultLocale();
}
